import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Sparkles,
  Calendar,
  Boxes,
  Tag,
  Recycle,
  BookOpen,
  Layers,
  Trash2,
  Moon,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { getYvelinesCityPageBySlug } from "@/data/yvelinesCitiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Service de livraison à Poissy ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Service de livraison de cartons de déménagement {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que votre temps est précieux et que transporter des
            piles de cartons volumineuses dans votre voiture est une
            corvée fatigante, notre service de livraison s&apos;occupe
            de tout.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Couverture locale totale",
              body: "Nous livrons directement au pied de votre immeuble ou sur le pas de votre porte à Poissy, ainsi que dans les communes voisines : Carrières-sous-Poissy, Achères, Villennes-sur-Seine, Orgeval et Chambourcy.",
              Icon: Truck,
            },
            {
              title: "Ponctualité rigoureuse",
              body: "Aucun risque de bloquer votre journée. Nous planifions ensemble un créneau horaire précis et strictement respecté pour s'adapter à vos contraintes de temps.",
              Icon: Calendar,
            },
            {
              title: "Fournitures irréprochables",
              body: "Tous vos cartons sont acheminés à plat, sous film de protection hermétique, garantissant un matériel propre, sec et immédiatement prêt à l'emploi.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons pour livres ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Cartons pour livres
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            La solidité face au poids de la culture
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            L&apos;une des erreurs les plus fréquentes lors d&apos;un
            déménagement est d&apos;utiliser de grands cartons classiques
            pour emballer des livres, ce qui les rend impossibles à
            soulever ou provoque la déchirure du fond. Nos cartons
            spécifiques apportent la solution.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Un format compact et ergonomique",
              body: "Conçus avec des dimensions idéales, ces cartons évitent l'accumulation d'un poids excessif. Ils restent faciles à manipuler, à soulever et à empiler.",
              Icon: BookOpen,
            },
            {
              title: "Une double cannelure renforcée",
              body: "Leur structure en carton lourd de qualité industrielle offre une résistance maximale à la déformation et à l'écrasement vertical, protégeant ainsi vos collections de livres, vos bandes dessinées, vos manuels scolaires ou vos dossiers de bureau les plus denses.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement?categorie=livres"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-poissy-cta-livres"
          >
            <BookOpen className="h-4 w-4" strokeWidth={2.6} />
            Voir les cartons spécial livres
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Cartons reconditionnés à -40% ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Exclusivité éco-malin
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Nos cartons reconditionnés à&nbsp;-40%
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pour les habitants {city.aCity} qui souhaitent concilier
              économies et respect de l&apos;environnement, nous avons
              mis en place une filière de reconditionnement unique et
              ultra-avantageuse.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Utilisés une seule et unique fois",
                  body: "Provenant directement de transferts d'entreprises ou de déménagements de standing, ces cartons professionnels n'ont servi qu'une seule fois. Ils affichent un état de robustesse identique au neuf.",
                  Icon: Recycle,
                },
                {
                  title: "Triés manuellement par nos experts",
                  body: "Chaque boîte passe un contrôle qualité strict. Nous éliminons rigoureusement tout élément présentant une faiblesse ou une pliure pour ne vous proposer que le meilleur.",
                  Icon: ShieldCheck,
                },
                {
                  title: "Une économie immédiate de -40%",
                  body: "Vous bénéficiez de la solidité et de la sécurité d'un carton de qualité industrielle à un tarif d'occasion imbattable. C'est l'offre la plus intelligente du marché !",
                  Icon: Tag,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-reconditionnes"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-poissy-cta-reconditionnes"
            >
              <Recycle className="h-4 w-4" strokeWidth={2.6} />
              Voir les cartons reconditionnés
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Tag className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                -40% sur du pro
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Toute la robustesse d&apos;un carton industriel neuf, au
              prix d&apos;occasion. Triés à la main, contrôlés un à un.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Un seul voyage au compteur
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Contrôle qualité à l&apos;unité
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Double cannelure conservée
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Geste éco-responsable
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Conseils d'organisation ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Conseils d&apos;organisation
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Les secrets d&apos;un emballage méthodique
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour éviter le stress des derniers jours et aborder le jour J
            en toute décontraction, une bonne organisation est
            essentielle.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Le tri systématique en amont",
              body: "Ne déménagez pas ce qui ne vous sert plus. Profitez-en pour trier, donner ou vendre. Moins vous aurez de volume, moins vous aurez besoin de cartons, et plus le chargement du camion sera rapide.",
              Icon: Trash2,
            },
            {
              title: "L'étiquetage intelligent",
              body: "Notez au marqueur sur le côté de chaque carton (et non sur le dessus, car les cartons seront empilés) la pièce de destination et un résumé rapide du contenu. Un code couleur par pièce fait gagner un temps précieux lors du déchargement.",
              Icon: Tag,
            },
            {
              title: "Le carton de première nécessité",
              body: "Préparez un carton « Kit de survie » à garder avec vous le jour J : chargeurs de téléphone, papiers importants, trousse de toilette, quelques vêtements de rechange et les ustensiles de base pour la première soirée dans votre nouveau logement.",
              Icon: Moon,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-poissy-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos kits complets
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-poissy-cta-accessoires"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; accessoires
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 6 — Couverture département / Satellites ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Couverture {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos zones de livraison de cartons dans les Yvelines (78)
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous proposons la livraison de cartons de déménagement et de
            matériel d&apos;emballage dans de nombreuses communes des
            Yvelines. Grâce à notre service de proximité, vous recevez
            rapidement les fournitures nécessaires pour préparer votre
            déménagement en toute simplicité.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Retrouvez ci-dessous les principales villes du département où
            nous assurons régulièrement la livraison de nos cartons et
            accessoires de déménagement à proximité de {city.name}&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getYvelinesCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement-${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-poissy-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-poissy-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#EEF3F7] border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-poissy-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
